import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Anzahlung in der Auftragsbestaetigung (05.10.2026, Ralph; 06.10.2026 umgestellt):
// zeigt vereinbart / gebucht / offen, Anzahlungsrechnungen und Zahlungsvermerke.
// Gebucht wird NUR in der Buchhaltung (offener Posten "Anzahlungsforderung").
// Ist noch etwas offen, kann eine Antwortkarte (Erinnerung, bar bei Arbeitsbeginn) gesendet werden.
const euro = (v) => Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const datum = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; } };

export const AnzahlungPanel = ({ orderId, depositAmount, vorschlag, reloadKey, onStand, onKarteSenden, navigate, vorSpeichern }) => {
  const [stand, setStand] = useState(null);
  const [karten, setKarten] = useState(null);       // != null -> Kartenauswahl
  const [mitPdf, setMitPdf] = useState(true);

  const laden = useCallback(async () => {
    try {
      const res = await api.get(`/orders/${orderId}/anzahlung`);
      setStand(res.data); onStand && onStand(res.data);
    } catch { /* ohne Finanzrecht oder neu: nichts anzeigen */ }
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { laden(); }, [laden, reloadKey]);
  // neuer Stand nach einer Buchung in einem anderen Fenster/Tab
  useEffect(() => {
    const neuLaden = () => laden();
    window.addEventListener("focus", neuLaden);
    return () => window.removeEventListener("focus", neuLaden);
  }, [laden]);

  if (!stand) return null;
  const vereinbart = Number(depositAmount || 0);
  const erhalten = Number(stand.erhalten || 0);
  const offen = Math.max(0, Math.round((vereinbart - erhalten) * 100) / 100);

  // "Zur Buchhaltung": AB vorher still speichern (ohne Rueckfragen), damit die Buchhaltung
  // die aktuelle Anzahlung sieht und nichts verloren geht. Klappt das Speichern nicht: bleiben.
  const zurBuchhaltung = async () => {
    if (vorSpeichern) {
      const ok = await vorSpeichern();
      if (!ok) { toast.error("Speichern hat nicht geklappt – bitte zuerst speichern"); return; }
    }
    navigate("/buchhaltung?tab=posten");
  };

  const kartenZeigen = async () => {
    try {
      const res = await api.get("/anzahlung/karten");
      setKarten((res.data || []).filter(k => k.doc_type === "auftrag"));
    } catch { toast.error("Antwortkarten konnten nicht geladen werden"); }
  };

  return (
    <div className="px-4 lg:px-10 py-3 border-t bg-amber-50/40" data-testid="anzahlung-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Anzahlung</span>
        <div className="flex gap-2">
          {offen > 0 && (
            <button type="button" onClick={zurBuchhaltung}
              className="px-3 py-1 text-xs border rounded-sm bg-white hover:bg-muted" data-testid="btn-anzahlung-buchhaltung"
              title="Zahlungseingänge werden in der Buchhaltung unter „Offene Posten“ gebucht">
              Zur Buchhaltung
            </button>
          )}
          {offen > 0 && (
            <button type="button" onClick={kartenZeigen}
              className="px-3 py-1 text-xs border rounded-sm bg-white hover:bg-muted" data-testid="btn-anzahlung-karte">
              Antwortkarte senden
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-sm max-w-md">
        <div><div className="text-[10px] text-muted-foreground uppercase">vereinbart</div><div className="font-mono" data-testid="anzahlung-vereinbart">{euro(vereinbart)}</div></div>
        <div><div className="text-[10px] text-muted-foreground uppercase">gebucht</div><div className="font-mono text-green-700" data-testid="anzahlung-erhalten">{euro(erhalten)}</div></div>
        <div><div className="text-[10px] text-muted-foreground uppercase">offen</div><div className={`font-mono ${offen > 0 ? "text-amber-700 font-semibold" : ""}`} data-testid="anzahlung-offen">{euro(offen)}</div></div>
      </div>
      {vorschlag && <p className="text-[11px] text-muted-foreground mt-1">Vorschlag 30 % des Auftragswerts – bitte prüfen, wird beim Speichern übernommen.</p>}
      {vereinbart > 0 && <p className="text-[11px] text-muted-foreground mt-1">Zahlungseingänge bucht die Buchhaltung (Offene Posten → „Anzahlungsforderung“). Der Stand hier aktualisiert sich danach von selbst.</p>}
      {stand.anzahlungsrechnungen?.length > 0 && (
        <ul className="mt-2 text-xs space-y-0.5">
          {stand.anzahlungsrechnungen.map(a => (
            <li key={a.id}>
              <button type="button" className="text-primary hover:underline" onClick={() => navigate(`/invoices/edit/${a.id}`)}>
                Anzahlungsrechnung {a.nummer}
              </button> vom {datum(a.datum)} · {euro(a.brutto)} · {a.status}
            </li>
          ))}
        </ul>
      )}
      {stand.vermerke?.length > 0 && (
        <ul className="mt-2 text-xs text-muted-foreground space-y-0.5" data-testid="anzahlung-vermerke">
          {stand.vermerke.map(v => <li key={v.id}>{datum(v.datum)} – {v.text}</li>)}
        </ul>
      )}

      {/* Dialog ueber Portal: das Dokument ist gezoomt (transform), "fixed" wuerde sonst daran haengen */}
      {karten && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="anzahlung-karten-dialog">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md">
            <div className="p-5 border-b">
              <h3 className="text-lg font-semibold">Antwortkarte wählen</h3>
              <p className="text-sm text-muted-foreground mt-1">Danach öffnet sich „Senden" mit diesem Text.</p>
            </div>
            <div className="p-5 space-y-2">
              {karten.length === 0 && <p className="text-sm text-muted-foreground">Keine Antwortkarten vorhanden (Einstellungen → Textvorlagen).</p>}
              {karten.map(k => (
                <button key={k.id} type="button" onClick={() => { setKarten(null); onKarteSenden(k, mitPdf); }}
                  className="w-full text-left px-3 py-2 text-sm border rounded-sm hover:bg-muted" data-testid="btn-anzahlung-karte-wahl">
                  {k.title}
                </button>
              ))}
              <label className="flex items-center gap-2 text-sm pt-2">
                <input type="checkbox" checked={mitPdf} onChange={(e) => setMitPdf(e.target.checked)} data-testid="chk-karte-mit-pdf" />
                AB als PDF anhängen
              </label>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button onClick={() => setKarten(null)} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted">Abbrechen</button>
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  );
};
